import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { IRootScope } from '../../IRootScope';
import {
    ApprovalStatus,
    ComponentDefinition,
    Group,
    IApproval,
    IApprovalRow,
    IApprovalStatusDto,
    IGuid,
    ISaveApprovalStatusDto,
    ITreeItem
} from '../../models';
import {
    ApprovalTableConfigReadTuple,
    IApprovalTableConfigForStrategy,
    IGroupApprovalService,
    IGroupService,
    ITranslatedLoggerService,
    IUserSettingsService,
    IValidationPageConfigurationService
} from '../../services';
import { refreshClick } from '../../common/events';
import { guidEmpty, momentFormat } from '../../common/constants';
import { ApprovalJustificationGetter } from '..';
import { getApprovalStatus } from './getApprovalStatus';

const userSettingsId = 'groupTreeFilter.maxDisplayLevel';

interface IGroupsTreeApprovalParam {
    groupGuid: string;
}

export interface IGroupsTreeApprovalCtrl {
    cancel: () => void;
    displaySlider?: boolean;
    isDirty: boolean;
    maxDepth: number;
    maxDisplayLevel: number;
    maxLevel: number;
    save: () => void;
}

class GroupsTreeApprovalController implements angular.IComponentController, angular.IOnChanges {
    beginDate;
    columns: string[] = [];
    configuration: ApprovalTableConfigReadTuple;
    ctrl: IGroupsTreeApprovalCtrl;
    endDate;
    getApprovalJustification: ApprovalJustificationGetter;
    histories: { [date: string]: string };
    initialDate: Date;
    isLoading: boolean;
    preselectGroupGuid: string;
    rows: IApprovalRow[];
    selectedRow: IApprovalRow;
    selectedStrategy: string;
    
    readonly isDisabled: boolean;

    private _allGroups: { [guid: string]: Group };
    private _backupRows: any[];
    private _groupsWithWidgets: { [guid: string]: Group };
    private _toSave: { [guid: string]: ISaveApprovalStatusDto };

    static $inject = [
        '$scope',
        '$rootScope',
        '$q',
        '$stateParams',
        'userSettingsService',
        'groupApprovalService',
        'groupService',
        'validationPageConfigurationService',
        'translatedLoggerService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$q: angular.IQService,
        private _$stateParams: IGroupsTreeApprovalParam,
        private _userSettingsService: IUserSettingsService,
        private _groupApprovalService: IGroupApprovalService,
        private _groupService: IGroupService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _loggerService: ITranslatedLoggerService
    ) {
    }

    $onInit() {
        this.isLoading = true;
        this.preselectGroupGuid = this._$stateParams.groupGuid;
        this.ctrl = {
            isDirty: false,
            cancel: () => this.reset(),
            save: () => this.save(),
            maxLevel: 1,
            maxDepth: 1,
            maxDisplayLevel: 1
        };

        this._$scope.$on(refreshClick, (event, data) => {
            if (data) {
                this.beginDate = moment(data.beginDate).startOf('d').toISOString();
                this.endDate = data.endDate;
            } // else use preexisting values
            if (this.rows) {
                this.refresh();
            }
        });

        this._$scope.$watch(() => this.ctrl.maxDisplayLevel, (newValue, oldValue) => {
            if (newValue !== oldValue && newValue <= this.ctrl.maxDepth) {
                this._userSettingsService.setApplicationSetting(userSettingsId, this.ctrl.maxDisplayLevel);
            }
        });

        this._$scope.$watch(() => this.ctrl, newValue => {
            if (newValue && this.rows) {
                this.loadCurrentlevel(this.rows);
            }
        });
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj['selectedStrategy'] && onChangesObj['selectedStrategy'].currentValue) {
            this.loadConfiguration(this.selectedStrategy);
        }
    }

    private loadConfiguration(strategyGuid: string) {
        this._$q
            .all([
                this._groupApprovalService.getConfigurationForStrategy(strategyGuid),
                this._validationPageConfigurationService.getGroupsWithWidgets(),
                this._groupService.getAll()
            ])
            .then(results => {
                const tableConfiguration = results[0];
                this.columns = tableConfiguration.identifierNames;
                this.configuration = tableConfiguration.approvalValuesReadTuple;
                this.loadCurrentlevel(tableConfiguration.rows);
                this.ctrl.displaySlider = this.configuration.isTreeMode;
                this._groupsWithWidgets = this.toDictionary(results[1]);
                this._allGroups = this.toDictionary(results[2].map(g => g.group));

                if (this.configuration.isTreeMode) {
                    this.loadTree(tableConfiguration)
                        .then(rows => {
                            this.rows = rows;
                            this.refreshAfterLoading();
                        })
                        .catch((reason) => {
                            this._loggerService.logError('Error while loading tree of approval', true, null,
                                String(reason.message ? reason.message : reason));
                            this.isLoading = false;
                        });
                } else {
                    // old flat presentation
                    const emptyArray = tableConfiguration.identifierNames.map((d, i) => i);
                    const rows = tableConfiguration.rows.map(d => {
                        const res = this.mapActualRow(d, emptyArray);
                        res.approvable = true;
                        return res;
                    });
                    this.rows = rows;
                    this.refreshAfterLoading();
                }
            });
    }

    private toDictionary<T extends IGuid>(list: T[]): { [guid: string]: T } {
        const index: { [guid: string]: T } = {};
        list.forEach(l => index[l.guid] = l);
        return index;
    }

    private loadTree(data: IApprovalTableConfigForStrategy): angular.IPromise<IApprovalRow[]> {
        return this._groupService.getAllAsTree()
            .then(treeItems => {
                // index existing row
                const emptyArray = data.identifierNames.map((d, i) => i);
                const rowIndex: { [guid: string]: IApprovalRow } = {};
                data.rows.forEach(d => {
                    rowIndex[d.group.guid] = this.mapActualRow(d, emptyArray);
                });

                // index treeItems and VM structure
                const treeIndex: { [guid: string]: ITreeItem } = {};
                const vmTreeIndex: { [guid: string]: IApprovalRow } = {};
                treeItems.forEach(t => {
                    treeIndex[t.guid] = t;
                    vmTreeIndex[t.guid] = rowIndex[t.guid];
                    // reassign depth if there is one
                    if (vmTreeIndex[t.guid]) {
                        vmTreeIndex[t.guid].depth = t.depth;
                        vmTreeIndex[t.guid].approvable = true;
                        vmTreeIndex[t.guid].parentGuid = t.parent.guid;
                        const paddingLeft = (vmTreeIndex[t.guid].depth - 1) * 15 + 'px';
                        vmTreeIndex[t.guid].style = { 'padding-left': paddingLeft };
                    }
                });

                // Sort by descending depth of existing VM
                const sortedRows = _.sortBy(_.filter(vmTreeIndex, t => t !== undefined), t => t.depth);
                sortedRows.reverse();
                this.ctrl.maxDepth = _.max(sortedRows.map(r => r.depth));
                if (this.ctrl.maxDepth) {
                    const savedLevel = this._userSettingsService
                        .getApplicationSetting<number>(userSettingsId);
                    this.ctrl.maxDisplayLevel = (savedLevel) ? savedLevel : this.ctrl.maxDepth;
                }
                // iterate them and get the hierarchy builded with status propagation
                sortedRows.forEach(r => {
                    for (let item = treeIndex[r.group.guid]; item.parent; item = item.parent) {
                        if (vmTreeIndex[item.guid]) {
                            if (!vmTreeIndex[item.guid].approvable) {
                                // TODO propagate status AND levelCount (and decimals?)
                                // TODO or not, only when we got data ?
                            }
                        } else {
                            vmTreeIndex[item.guid] = this.mapFakeRow(item, r.levelCount, 0, emptyArray);
                        }
                    }
                });

                return _.values<IApprovalRow>(vmTreeIndex).filter(v => v);
            });
    }

    private mapFakeRow(d: ITreeItem, levelCount, decimals, emptyArray): IApprovalRow {
        (d as any).hasValidation = false;
        return {
            aggregated: [],
            aggregatedValues: emptyArray,
            currentUserLevels: [],
            decimals: decimals,
            depth: d.depth ? d.depth : 0,
            group: d as any,
            guidValueAggregations: [],
            levelCount: levelCount,
            parentGuid: d.parent.guid,
            statuses: [],
            values: [],
        };
    }

    private mapActualRow(d, emptyArray: number[]): IApprovalRow {
        const group = this._allGroups[d.group.guid];
        d.group.hasValidation = d.group.hasValidation || this._groupsWithWidgets[d.group.guid];
        return {
            group: d.group,
            guidValueAggregations: d.guidValueAggregations.map(dd => dd.guid +
                dd.value +
                dd.aggregationMode),
            values: [],
            aggregatedValues: emptyArray,
            statuses: [],
            aggregated: [],
            currentUserLevels: d.currentUserLevels,
            levelCount: d.levelCount,
            decimals: d.decimals,
            depth: d.depth ? d.depth : 0,
            parentGuid: undefined,
            strategyGuid: group.approvalStrategyGuid,
        };
    }

    private refreshAfterLoading() {
        if (this.beginDate && this.endDate) {
            this.isLoading = false;
            this.refresh();
        } else if (this.initialDate) {
            this.beginDate = moment(this.initialDate).toDate();
            this.endDate = moment(this.initialDate).add(1, 'month').toDate();
            this.isLoading = false;
            this.refresh();
        }
    }

    select(row: IApprovalRow, $event?: angular.IAngularEvent) {
        if (row.approvable || !this.configuration.isTreeMode) {
            row.promise = this._groupApprovalService
                .getDetails(row.group.guid,
                    moment(this.beginDate).startOf('d').toISOString(),
                    this.endDate,
                    this.configuration
                )
                .then(data => {
                    return this.prepareData([row], data);
                });

            this.selectedRow = row;
        }

        if ($event && $event.preventDefault) {
            $event.preventDefault();
        }
    }

    changeStatus(approval: IApproval, status: ApprovalStatus, row: IApprovalRow): angular.IPromise<void> {
        if (row.approvable) {
            if (!approval.changed) {
                if (approval.status !== status) {
                    return this.applyStatus(approval, status, row);
                }
            } else {
                return this.revertStatus(approval, row);
            }
        }
        return this._$q.when();
    }

    stop(event: angular.IAngularEvent) {
        event.stopPropagation();
    }

    private save() {
        const array = _.map(this._toSave, t => t);
        this._groupApprovalService.saveStatuses(array)
            .then(message => {
                if (!!message) {
                    this._loggerService.logError(message);
                } else {
                    this._loggerService.logSuccess('Saved');
                    this._$rootScope.$broadcast(refreshClick);
                }
            })
            .catch(reason => {
                this._loggerService.logError('Save of approbation failed.', true, null,
                    String(reason.message ? reason.message : reason));
            });
    }

    private reset() {
        this.rows = this._backupRows.map(this.copyApprovalRow);
        this._toSave = {};
        this.ctrl.isDirty = false;
    }

    private revertStatus(approval: IApproval, row: IApprovalRow): angular.IPromise<void> {
        delete this._toSave[row.group.guid];
        approval.status = approval.original;
        approval.changed = false;
        this.ctrl.isDirty = Object.keys(this._toSave).length > 0;
        return this._$q.when();
    }

    // approval: IApproval, status: ApprovalStatus,
    private applyStatus(approval: IApproval, status: ApprovalStatus, row: IApprovalRow): angular.IPromise<void> {
        return this._groupApprovalService
            .isJustificationRequired(approval.level, status, row.strategyGuid)
            .then(justificationRequired => {
                if (justificationRequired) {
                    this.getApprovalJustification(status === ApprovalStatus.Rejected, row.group.uniqueName, moment(this.beginDate).format(momentFormat))
                        .then(comment => {
                            this.applyJustifiedStatus(approval, status, row, comment);
                        })
                        .catch(() => {
                            // no comment was entered, don't do anything
                        });
                } else {
                    this.applyJustifiedStatus(approval, status, row, undefined);
                }
            });
    }

    private applyJustifiedStatus(approval, status, row, comment) {
        // for UI display
        approval.changed = true;
        approval.original = approval.status;
        approval.status = status;
        this.ctrl.isDirty = true;

        // for saving
        this._toSave[row.group.guid] = {
            beginDate: this.beginDate,
            comment: comment,
            endDate: this.endDate,
            groupGuid: row.group.guid,
            level: approval.level,
            status: status
        };
    }

    private refresh() {
        this.isLoading = true;
        return this._groupApprovalService.getStatuses(
            moment(this.beginDate).startOf('d').toISOString(),
            this.endDate,
            this.configuration
        )
            .then(data => {
                const copy = this.prepareData(this.rows, data);
                this._backupRows = copy;
                // No need to reset, copy is already a copy of the rows
                this.rows = copy;
                this._toSave = {};
                this.ctrl.isDirty = false;
                this.tryReselect();
            })
            .finally(() => this.isLoading = false);
    }

    private tryReselect(): void {
        if (this.rows.length > 0) {

            let guid = guidEmpty;
            if (this.selectedRow) {
                guid = this.selectedRow.group.guid;
            } else if (this.preselectGroupGuid) {
                guid = this.preselectGroupGuid;
            }

            let found = _.find(this.rows, g => guid === g.group.guid);
            if (!found) {
                found = _.find(this.rows, r => !!r.approvable);
            }
            this.select(found ?? this.rows[0]);
        }
    }

    private computeApprobationStatus(row: IApprovalRow, approb: IApprovalStatusDto) {
        const aggregatedValues = [];
        row.guidValueAggregations.forEach(d => {
            let found = _.find(approb.aggregatedValues, v => v.guid + v.valueType
                + v.aggregationMode === d);
            if (!found) {
                found = {
                    guid: '',
                    valueType: '',
                    aggregationMode: ''
                };
            }
            aggregatedValues.push(found);
        });
        const statuses = approb.statuses;
        row.dates = statuses.map((d) => {
            const filter = approb.values.filter(v => v.timeStamp === d.timeStamp);
            const values = [];
            row.guidValueAggregations.forEach(dd => {
                let existing = _.find(filter, v => v.guid + v.valueType + v.aggregationMode === dd);
                if (!existing) {
                    existing = {
                        timeStamp: d.timeStamp,
                        value: null
                    };
                }
                values.push(existing);
            });
            return {
                timeStamp: moment(d.timeStamp).toISOString(),
                approvals: d.approvals,
                values: values
            };
        });

        row.histories = approb.histories;
        const index = [];
        if (row.histories && row.histories.length > 0) {
            row.histories.forEach(h => {
                index.push(...h.approvals);
            });
        }
        row.historyIndex = index;
        row.aggregated = approb.aggregated;
        row.aggregatedValues = aggregatedValues;
    }

    private prepareData(rows: IApprovalRow[], data: IApprovalStatusDto[]): IApprovalRow[] {
        const copy: IApprovalRow[] = rows.map(this.copyApprovalRow);
        const indexData = this.indexByGuid(data);

        // iterate on reverse order
        for (let i = copy.length - 1; i >= 0; i--) {
            const row = copy[i];
            const approb = indexData[row.group.guid];

            if (approb) {

                if (approb.propagated) {
                    row.aggregated = approb.aggregated;
                } else {
                    this.computeApprobationStatus(row, approb);
                }

                if (this.configuration.isTreeMode) {
                    this.propagateToParent(row, indexData);
                }
            }

            if (row.aggregatedValues && row.aggregatedValues.length > 0) {
                row.hasData = _(row.aggregatedValues)
                    .map('value')
                    .filter(v => v !== undefined && v !== null)
                    .value()
                    .length > 0;
            }
        }

        return copy;
    }

    private copyApprovalRow(r: IApprovalRow): IApprovalRow {
        const retval: IApprovalRow = {} as any;
        Object.getOwnPropertyNames(r).forEach(key => {
            retval[key] = r[key];
        });
        return retval;
    }

    private propagateToParent(row: IApprovalRow, indexData: { [guid: string]: any }) {
        const parentStatus = indexData[row.parentGuid];

        if (!parentStatus) {
            // creating new parent status according to mine
            indexData[row.parentGuid] = { propagated: true, aggregated: angular.copy(row.aggregated) };
        } else if (parentStatus.propagated && row.aggregated) {
            // updating parent status by merging

            row.aggregated.forEach(rowStatus => {
                const found = _.find(parentStatus.aggregated as any[], s => s.level === rowStatus.level);
                if (!found) {
                    parentStatus.aggregated.push(angular.copy(rowStatus));
                } else {
                    found.status = getApprovalStatus(found.status, rowStatus.status);
                    this.updateRecentStatus(found, rowStatus);
                }
            });
        } else {
            console.warn('Propagation of status interrupted since group has his own approval strategy');
        }
    }

    private updateRecentStatus(original: IApproval, row: IApproval) {
        if (row.approvedDate &&
            (!original.approvedDate || moment(row.approvedDate).isAfter(moment(original.approvedDate)))) {
            original.approvedDate = row.approvedDate;
            original.user = row.user;
        }
    }

    private indexByGuid<T>(data: T[]): { [guid: string]: T } {
        const result: { [guid: string]: any } = {};
        data.forEach(d => result[(d as any).guid] = d);
        return result;
    }

    private loadCurrentlevel(rows: any[]) {
        this.ctrl.maxLevel = _.max(rows.map(r => r.levelCount));
        if (this.ctrl.maxLevel) {
            const savedLevel = this._userSettingsService
                .getApplicationSetting<number>(userSettingsId);
            this.ctrl.maxDisplayLevel = (savedLevel) ? savedLevel : this.ctrl.maxLevel;
        }
    }
}

export const wtGroupsTreeApproval: ComponentDefinition = {
    componentId: 'wtGroupsTreeApproval',
    controller: GroupsTreeApprovalController,
    template: require('./groupsTreeApproval.component.html'),
    controllerAs: 'vm',
    bindings: {
        columns: '=',
        ctrl: '=',
        getApprovalJustification: '<',
        initialDate: '=?',
        isDisabled: '<',
        selectedRow: '=',
        selectedStrategy: '<?'
    }
};